import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";

const DOCTYPES = [
  { key: "sale",     title: "Sales",      description: "Every accepted purchase gets this number." },
  { key: "proforma", title: "Pro-formas", description: "Quotation / preliminary invoice number series." },
  { key: "invoice",  title: "Invoices",   description: "Prefix here is the fallback. Each invoice may still use its own prefix (e.g. STRIOR for Stripe)." },
  { key: "receipt",  title: "Receipts",   description: "Official Receipt (OR) number sent to customers on the emailed receipt." },
];

const YEAR_FMTS = [
  ["YYYY",   "YYYY  (2026)"],
  ["YY",     "YY    (26)"],
  ["YYYYMM", "YYYYMM (202609)"],
  ["YYMM",   "YYMM  (2609)"],
  ["none",   "No year token"],
];
const SEPARATORS = [["-", "- (dash)"], ["/", "/ (slash)"], ["_", "_ (underscore)"], [".", ". (dot)"]];
const RESETS = [["yearly", "Yearly  (Jan 1 = 00001)"], ["monthly", "Monthly (every month starts at 001)"], ["never", "Never   (continuous)"]];
const PADDINGS = [3, 4, 5, 6, 7, 8];

function buildPreview({ prefix = "", separator = "-", year_format = "YYYY", padding = 5 }, seq = 1) {
  const now = new Date();
  let tok = "";
  const y = now.getFullYear(), yy = String(y).slice(2), m = String(now.getMonth() + 1).padStart(2, "0");
  if (year_format === "YYYY") tok = `${y}`;
  else if (year_format === "YY") tok = yy;
  else if (year_format === "YYYYMM") tok = `${y}${m}`;
  else if (year_format === "YYMM") tok = `${yy}${m}`;
  const seqStr = String(seq).padStart(Math.max(3, Math.min(8, padding)), "0");
  return tok ? `${prefix}${separator}${tok}${separator}${seqStr}` : `${prefix}${separator}${seqStr}`;
}

export default function NumberSeriesSettings() {
  const { user } = useAuth();
  const [config, setConfig] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/number-series");
      setConfig(data);
      setDirty({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = (doctype, field, value) => {
    setDirty((d) => ({ ...d, [doctype]: { ...(config[doctype] || {}), ...(d[doctype] || {}), [field]: value } }));
  };

  const save = async (doctype) => {
    const merged = { ...(config[doctype] || {}), ...(dirty[doctype] || {}) };
    const payload = {
      prefix: (merged.prefix || "").trim(),
      separator: merged.separator || "-",
      year_format: merged.year_format || "YYYY",
      padding: Number(merged.padding) || 5,
      reset: merged.reset || "yearly",
      note: merged.note || null,
    };
    if (!payload.prefix) { toast.error("Prefix cannot be empty"); return; }
    setSaving((s) => ({ ...s, [doctype]: true }));
    try {
      const { data } = await api.put(`/settings/number-series/${doctype}`, payload);
      setConfig((c) => ({ ...c, [doctype]: data }));
      setDirty((d) => { const { [doctype]: _, ...rest } = d; return rest; });
      toast.success(`${doctype} number series saved`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving((s) => ({ ...s, [doctype]: false })); }
  };

  if (user?.role !== "superadmin") {
    return <PageBody><p className="text-sm text-slate-500">Super Admin only.</p></PageBody>;
  }

  return (
    <PageBody className="space-y-6" data-testid="number-series-page">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Document number formats</CardTitle>
          <CardDescription>
            Configure how sales, pro-formas, invoices and receipts are numbered. Changes apply to <em>new</em> documents from the moment you save —
            existing numbers stay as they are. Sequences already in use continue counting forward.
          </CardDescription>
        </CardHeader>
      </Card>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {DOCTYPES.map(({ key, title, description }) => {
            const merged = { ...(config[key] || {}), ...(dirty[key] || {}) };
            const preview = buildPreview(merged, 1);
            const previewNext = buildPreview(merged, 42);
            const isDirty = !!dirty[key];
            return (
              <Card key={key} data-testid={`number-series-card-${key}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle className="text-sm">{title}</CardTitle>
                      <CardDescription className="text-xs mt-1">{description}</CardDescription>
                    </div>
                    <Badge variant="outline" className="font-mono text-xs" data-testid={`number-series-preview-${key}`}>{preview}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Prefix</Label>
                      <Input
                        value={merged.prefix || ""}
                        onChange={(e) => patch(key, "prefix", e.target.value)}
                        placeholder="e.g. OR"
                        maxLength={16}
                        className="font-mono"
                        data-testid={`number-series-prefix-${key}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Separator</Label>
                      <Select value={merged.separator || "-"} onValueChange={(v) => patch(key, "separator", v)}>
                        <SelectTrigger data-testid={`number-series-separator-${key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{SEPARATORS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Year token</Label>
                      <Select value={merged.year_format || "YYYY"} onValueChange={(v) => patch(key, "year_format", v)}>
                        <SelectTrigger data-testid={`number-series-year-${key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{YEAR_FMTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Digits (padding)</Label>
                      <Select value={String(merged.padding || 5)} onValueChange={(v) => patch(key, "padding", Number(v))}>
                        <SelectTrigger data-testid={`number-series-padding-${key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{PADDINGS.map((n) => <SelectItem key={n} value={String(n)}>{n} digits</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Reset sequence</Label>
                    <Select value={merged.reset || "yearly"} onValueChange={(v) => patch(key, "reset", v)}>
                      <SelectTrigger data-testid={`number-series-reset-${key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{RESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Internal note (optional)</Label>
                    <Textarea
                      rows={2}
                      value={merged.note || ""}
                      onChange={(e) => patch(key, "note", e.target.value)}
                      placeholder="Explain the convention for the ops team"
                      data-testid={`number-series-note-${key}`}
                    />
                  </div>
                  <div className="rounded border bg-slate-50 p-3 text-xs space-y-1">
                    <div className="text-slate-500 uppercase tracking-wider">Live preview</div>
                    <div className="flex items-center gap-4 font-mono">
                      <span>First: <strong data-testid={`number-series-preview-first-${key}`}>{preview}</strong></span>
                      <span className="text-slate-400">→</span>
                      <span>42nd: <strong data-testid={`number-series-preview-next-${key}`}>{previewNext}</strong></span>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="ghost" size="sm" disabled={!isDirty || saving[key]} onClick={() => setDirty((d) => { const { [key]: _, ...rest } = d; return rest; })} data-testid={`number-series-revert-${key}`}>Revert</Button>
                    <Button size="sm" disabled={!isDirty || saving[key]} onClick={() => save(key)} data-testid={`number-series-save-${key}`}>{saving[key] ? "Saving…" : "Save"}</Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </PageBody>
  );
}
